import { Observable } from "rxjs";
import { SCOPE, SCOPE_COMPANY } from "constants/action-types";
import siteConfig from "site-config";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import { add as snackbarAdd } from "actions/snackbars";
import { fetchProductStyleInsights } from "actions/insights/product-style-insights";
import { fetchProductBadges } from "actions/insights/product-badges";
import { clearInsightsData } from "actions/insights-actions";
import { IRequestAction, progressEpic } from "epics/base-epic";
import { normalizeData } from "helpers/data/normalize";
import { updateEntities } from "redux-query";
import querystring from "querystring";
import { Companies as CompaniesSchema } from "schemas/companies";
import { ICompany } from "components/contacts/contacts-types";
import { isInsightsEnabledWithCurrency } from "reducers/insights-reducer";
import { syncFromCart } from "actions/cart";
import { setViewAs } from "actions/scope";

export const fetchCompaniesEpic = (action$, store) => {
    return action$
        .ofType(SCOPE.COMPANIES.START)
        .map(({ payload }) => payload)
        .flatMap(({ filters, meta }) => {
            const { scope, entities, cart } = store.getState();
            return ajax(`${siteConfig.api}/api/v3.0/companies?${querystring.stringify(filters)}`)
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const data = normalizeData(response, CompaniesSchema, entities.schemas, scope);
                        return { error: false, data, meta };
                    } else {
                        return { error: true, data: null, meta };
                    }
                })
                .mergeMap(({ error, data, meta }) => {
                    let actions = [];
                    const currentCompanies = {
                        ...(!meta.replace && scope.currentCompanies),
                        ...data.entities.companies,
                    };

                    if (!error) {
                        actions = [
                            updateEntities({
                                companies: (companies = {}) => {
                                    return {
                                        ...companies,
                                        ...data.entities.companies,
                                    };
                                },
                            }),
                            {
                                meta: {
                                    ...meta,
                                },
                                payload: {
                                    currentCompanies,
                                    results: data.result,
                                    nextPage: filters.__limit === data.result.length,
                                },
                                type: SCOPE.COMPANIES.COMPLETED,
                            },
                        ];
                        if (meta.resumeCartSync) {
                            let order;
                            let _meta = { updateRetailer: true } as any;

                            if (meta.order) {
                                actions.push(syncFromCart(meta.order, { ...meta, force: true }));
                            } else if (cart.isFetching) {
                                order = {
                                    retailer: {
                                        ref: undefined,
                                    },
                                };

                                actions.push(syncFromCart(order, { ..._meta, force: true }));
                            } else {
                                const orderId = cart.result;
                                order = cart.entities.orders[orderId];
                                if (!data.result.length) {
                                    // trying to hydrate company data from a company
                                    // that user no longer has access to
                                    _meta = {
                                        companyError: true,
                                        syncToCart: false,
                                    };
                                }

                                actions.push(syncFromCart(order, { ..._meta, force: true }));
                            }
                        }
                    }
                    return actions;
                })
                .catch((error) => {
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SCOPE.COMPANIES.COMPLETED,
                        }),
                        Observable.of(snackbarAdd({ message: error.message })),
                    );
                });
        });
};

export const fetchCompanyEpic = progressEpic<IRequestAction<ICompany>, ICompany>(
    SCOPE_COMPANY,
    ({ payload }) => ({
        method: "GET",
        url: siteConfig.api + payload.url,
    }),
    {
        dataCustomizer: (company, action, store) => {
            if (company?._id === action.payload.meta._id) {
                return { ...action.payload.meta, ...company };
            }
            // otherwise do nothing
            return action.payload.meta;
        },
        finishActions: (company) => [setViewAs(company)],
    },
);

export const fetchProductStylesWhenViewAsChangesEpic = (action$, store) =>
    action$.ofType(SCOPE_COMPANY.SUCCESS).mergeMap((action) => {
        const state = store.getState();

        if (!isInsightsEnabledWithCurrency(state)) {
            return [];
        }

        return [
            clearInsightsData(),
            fetchProductStyleInsights(action.payload._id),
            fetchProductBadges(action.payload._id),
        ];
    });
